'use client'

// State
import { useMemo, useState } from 'react'

// Animation
import { motion } from 'framer-motion'

// Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowUpRightFromSquare,
  faFileLines,
  faFlask,
  faNewspaper,
  faPlay,
} from '@fortawesome/free-solid-svg-icons'

// Next.js
import Link from 'next/link'

// design-studio
import {
  Container,
  fadeInUp,
  Section,
  SectionHeader,
  spaceGrotesk,
  staggerContainer,
} from 'design-studio'

// design-studio — shared canonical media list (single source of truth across
// all Imandra web apps). The full archive is rendered on /docs/media; the home
// section shows the newest few, filterable by type.
import {
  filterMedia,
  sortByDateDesc,
  type MediaItem,
  type MediaType,
} from 'design-studio/data/imandra-media'

const HOME_LIMIT = 6

const MEDIA: MediaItem[] = sortByDateDesc(
  filterMedia({ products: ['codelogician'] }),
)

const TYPE_CONFIG: Record<
  MediaType,
  { label: string; color: string; icon: typeof faFileLines }
> = {
  paper: {
    label: 'Research',
    color: 'text-violet-400 border-violet-400/30 bg-violet-400/10',
    icon: faFlask,
  },
  'white-paper': {
    label: 'White paper',
    color: 'text-violet-400 border-violet-400/30 bg-violet-400/10',
    icon: faFlask,
  },
  video: {
    label: 'Video',
    color: 'text-red-400 border-red-400/30 bg-red-400/10',
    icon: faPlay,
  },
  article: {
    label: 'Article',
    color: 'text-blue-400 border-blue-400/30 bg-blue-400/10',
    icon: faFileLines,
  },
  blog: {
    label: 'Blog',
    color: 'text-teal-400 border-teal-400/30 bg-teal-400/10',
    icon: faFileLines,
  },
  news: {
    label: 'News',
    color: 'text-amber-400 border-amber-400/30 bg-amber-400/10',
    icon: faNewspaper,
  },
}

const TYPE_ORDER: MediaType[] = [
  'news',
  'article',
  'blog',
  'video',
  'paper',
  'white-paper',
]

function MediaCard({ item }: { item: MediaItem }) {
  const cfg = TYPE_CONFIG[item.type]
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col rounded-xl border border-slate-700/40 bg-slate-800/20 p-5 backdrop-blur-sm transition-all duration-200 hover:border-teal-400/70 hover:bg-slate-800/40"
    >
      {/* Top: tag + date only */}
      <div className="mb-3 flex items-center justify-between gap-2 lg:mb-4">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[10px] tracking-widest uppercase lg:text-xs ${cfg.color}`}
        >
          <FontAwesomeIcon icon={cfg.icon} className="size-2.5 lg:size-3" />
          {cfg.label}
        </span>
        <span className="font-mono text-[10px] text-slate-400 lg:text-xs">
          {item.date}
        </span>
      </div>

      {/* Title — larger */}
      <h3 className="font-display mb-2 text-lg leading-snug font-light tracking-wide text-slate-200 transition-colors group-hover:text-white lg:text-xl">
        {item.title}
      </h3>

      {/* Blurb — smaller */}
      {item.description ? (
        <p className="font-display grow text-xs leading-relaxed text-slate-400 lg:text-sm">
          {item.description}
        </p>
      ) : (
        <span className="grow" aria-hidden />
      )}

      {/* Footer */}
      {item.source ? (
        <div className="mt-4 flex items-center gap-1.5 font-mono text-[10px] text-slate-400 transition-colors group-hover:text-slate-300 lg:text-xs">
          <FontAwesomeIcon
            icon={faArrowUpRightFromSquare}
            className="size-2.5 lg:size-3"
          />
          {item.source}
        </div>
      ) : null}
    </a>
  )
}

export function MediaSection() {
  const tabs = useMemo(() => {
    const present = new Set(MEDIA.map((i) => i.type))
    return TYPE_ORDER.filter((t) => present.has(t))
  }, [])
  const [selected, setSelected] = useState<MediaType | 'all'>('all')

  const visible = useMemo(() => {
    const filtered =
      selected === 'all' ? MEDIA : MEDIA.filter((i) => i.type === selected)
    return filtered.slice(0, HOME_LIMIT)
  }, [selected])

  return (
    <Section className="relative z-10 py-16 sm:py-24">
      <Container className={`max-w-8xl ${spaceGrotesk.className}`}>
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.1 }}
          className="space-y-12"
        >
          <SectionHeader
            pretitle="Research & Media"
            title={
              <>
                In the press <br className="hidden xl:block" /> and on paper
              </>
            }
            description="Peer-reviewed research, press coverage, and video walkthroughs of CodeLogician and the reasoning engine behind it."
          />

          {tabs.length > 1 ? (
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-2">
              {(['all', ...tabs] as const).map((tab) => {
                const isSelected = selected === tab
                return (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setSelected(tab)}
                    className={`rounded-full border px-3 py-1 font-mono text-xs tracking-widest uppercase transition-colors ${
                      isSelected
                        ? 'border-teal-400/70 bg-teal-400/10 text-teal-300'
                        : 'border-slate-700/50 text-slate-400 hover:border-teal-400/50 hover:text-teal-300'
                    }`}
                  >
                    {tab === 'all' ? 'All' : TYPE_CONFIG[tab].label}
                  </button>
                )
              })}
            </motion.div>
          ) : null}

          <motion.div
            variants={fadeInUp}
            className="mb-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {visible.map((item) => (
              <MediaCard key={item.url} item={item} />
            ))}
          </motion.div>

          <motion.p
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: '-50px 0px' }}
            className="mx-auto max-w-6xl text-base text-slate-300"
          >
            For more research, press, and videos, see the{' '}
            <Link
              href="/docs/media/"
              className="text-blue-400 underline-offset-2 hover:text-blue-300 hover:underline"
            >
              media page
            </Link>
            .
          </motion.p>
        </motion.div>
      </Container>
    </Section>
  )
}
